import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { seedAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Briefcase, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [branding, setBranding] = useState({ logoUrl: null, appName: 'Workforce OS' });
  const { login } = useAuth();
  const navigate = useNavigate();

  // Fetch branding settings on mount
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        // Fetch logo
        const logoRes = await axios.get(`${API_URL}/api/settings/logo`);
        // Fetch app name
        const nameRes = await axios.get(`${API_URL}/api/settings/app-name`);
        
        setBranding({
          logoUrl: logoRes.data.has_logo ? logoRes.data.logo_data : null,
          appName: nameRes.data.app_name || 'Workforce OS'
        });
      } catch (error) {
        console.error('Failed to fetch branding:', error);
      }
    };
    fetchBranding();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Welcome back!');
      // Navigation handled by App.js
    } else {
      toast.error(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  const handleSeedDemo = async () => {
    setSeeding(true);
    try {
      const response = await seedAPI.seedDemo();
      toast.success('Demo data created!', {
        description: `Owner: ${response.data.owner_email} / ${response.data.owner_password}`
      });
      setEmail(response.data.owner_email);
      setPassword(response.data.owner_password);
    } catch (err) {
      if (err.response?.data?.message?.includes('already seeded')) {
        toast.info('Demo data already exists', {
          description: 'Use owner@workforceos.com / owner123'
        });
        setEmail('owner@workforceos.com');
        setPassword('owner123');
      } else {
        toast.error('Failed to seed demo data');
      }
    }
    setSeeding(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Back to Home */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        {/* Logo */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-3">
            {branding.logoUrl ? (
              <img 
                src={branding.logoUrl} 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
            ) : (
              <Briefcase className="h-10 w-10 text-primary" />
            )}
            <span className="text-3xl font-bold text-primary" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {branding.appName}
            </span>
          </div>
          <p className="text-muted-foreground text-center">
            Multi-company workforce operating system
          </p>
        </div>

        {/* Login Card */}
        <Card className="border-border">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Sign in
            </CardTitle>
            <CardDescription>
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleSeedDemo}
              disabled={seeding}
              data-testid="seed-demo-btn"
            >
              {seeding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating demo data...
                </>
              ) : (
                'Load Demo Data'
              )}
            </Button>
            
            <div className="mt-4 text-xs text-muted-foreground text-center space-y-1">
              <p><strong>Demo accounts:</strong></p>
              <p>Owner: owner@workforceos.com / owner123</p>
              <p>Admin: Check seeded data for company admins</p>
              <p>Manager: manager passwords are &quot;manager123&quot;</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
